import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { BehaviorSubject, combineLatest, Subject, Subscription, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../constant/constant';
import { AppEventType } from '../enums/app-events.enum';
import { LOCAL_NOTIFICATION_ACTION_TYPE } from '../enums/notification/notification.enum';
import { LogUserPositionResponse, UserPositionResult } from '../models/log-user-position/log-user-position-response-interface';
import { AppUserClockStateService, ClockState, UserClockState } from './app-user-clock-state.service';
import { DeviceInfoService } from './device-info.service';
import { EventBusService } from './event-bus.service';
import { LocationService } from './location.service';
import { NotificationsService } from './notifications.service';
import { TelemetryContextService, TelemetrySnapshot } from '../baseServices/logging/telemetry-context.service';
import { ApplicationInsightsService, HttpDependencyOutcome } from '../baseServices/logging/application-insights.service';
import { LogService } from '../baseServices/logging/log.service';
import { telemetryUuid } from '../baseServices/logging/telemetry-identity.service';
import { hasValidCoordinates } from '../utils/coordinate.util';
import { TOAST_CLASS } from '../enums/notification/notification.enum';
import { AppStateService } from './app-state.service';
import { AuthSessionService } from './auth/auth-session.service';

interface WatcherContext {
  readonly id: string;
  readonly key: string;
  readonly snapshot: TelemetrySnapshot;
  readonly permissionMode: 'request' | 'check';
  sequence: number;
  lastAttempt?: number;
  providerId?: string;
}

export interface LocationObservation {
  readonly context: TelemetrySnapshot;
  readonly body: Readonly<Record<string, string | number | boolean | null>>;
}

export type LocationTrackingHealth = 'stopped' | 'starting' | 'active' | 'temporarily-unavailable' | 'permission-required';

@Injectable({
  providedIn: 'root',
})
export class LocationTrackingService implements OnDestroy {
  private readonly isNativePlatform = Capacitor.isNativePlatform();
  locationWatcherId: string;
  geoLocationPlugin: BackgroundGeolocationPlugin;

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private active?: WatcherContext;
  private retired?: WatcherContext;
  private lifecycle: Promise<void> = Promise.resolve();
  private destroyed = false;
  private retry?: { watcher: WatcherContext; controller: AbortController };
  private verificationOrder = 0;
  private appliedOrder = 0;
  private newestFix?: number;
  private appliedFix?: number;
  private cacheAccount?: string;
  private readonly errors = new Map<string, { count: number; timer?: ReturnType<typeof setTimeout> }>();
  private readonly healthSubject = new BehaviorSubject<LocationTrackingHealth>('stopped');
  /** Provider health; delivery outcomes remain in dependency and failure telemetry. */
  readonly health$ = this.healthSubject.asObservable();
  private recoveryTimer?: ReturnType<typeof setTimeout>;
  private recoveryDelayMs = 1000;
  private permissionRequiredKey?: string;
  private lifecycleRevision = 0;

  constructor(
    private deviceInfo: DeviceInfoService, 
    private notificationsService: NotificationsService, 
    private eventBusService: EventBusService,
    private locationService: LocationService,
    private appUserClockStateService: AppUserClockStateService,
    private context: TelemetryContextService,
    private telemetry: ApplicationInsightsService,
    private ngZone: NgZone,
    private appState: AppStateService,
    private sessions: AuthSessionService,
    private log: LogService
  ) {
    this.geoLocationPlugin = Capacitor.registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopLocationWatcher();
    this.errors.forEach(error => clearTimeout(error.timer));
    this.locationService.clearLastKnownLocation();
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.healthSubject.complete();
  }

  async loadSubscriptions() {
    const retrySubscription = this.eventBusService.on(AppEventType.RETRY_LOCATION_REQUESTED_BY_USER, () => this.refetchUserCurrentLocation());
    this.subscriptions.push(retrySubscription);
    this.appState.getAppState().pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state.isActive) { this.reconcileWorkingContext(this.appUserClockStateService.clockState); }
    });
    
    // Start tracking location if user is a clock time entry user and is currently working (not on break)
    // If the user is not a clock time entry user, location is not tracked
    combineLatest([this.appUserClockStateService.clockState$, this.context.accountChanges$])
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(([state]) => this.reconcileWorkingContext(state));
  }

  private reconcileWorkingContext(state: UserClockState | null): void {
    const account = this.context.capture().properties;
    const cacheAccount = [account.UserId, account.LoginContextId].join(':');
    if (this.cacheAccount !== cacheAccount) {
      this.locationService.clearLastKnownLocation();
      this.cacheAccount = cacheAccount;
    }
    const key = this.workingContextKey(state, account);
    if (key) {
      this.startLocationWatcher(key);
    } else {
      this.stopLocationWatcher();
    }
  }

  private workingContextKey(state: UserClockState | null, account: TelemetrySnapshot['properties']): string | undefined {
    if (state?.state !== ClockState.WORKING || !account.UserId || String(state.activeTimeRecord?.EmployeeId) !== account.UserId) { return undefined; }
    return [account.UserId, account.LoginContextId, state.sinceTime, state.activeTimeRecord?.RecordId, state.activeTimeRecord?.ProjectId].join(':');
  }

  async startLocationWatcher(key?: string, permissionMode: 'request' | 'check' = 'request') {
    const snapshot = this.context.capture();
    if (this.destroyed || !snapshot.properties.UserId) { return; }
    const contextKey = key || [snapshot.properties.UserId, snapshot.properties.LoginContextId].join(':');
    const permissionKey = [snapshot.properties.UserId, snapshot.properties.LoginContextId].join(':');
    const revision = this.lifecycleRevision;
    if (this.active?.key === contextKey) { return this.lifecycle; }
    if (this.permissionRequiredKey === permissionKey) {
      try {
        const permission = await Geolocation.checkPermissions();
        if (permission.location !== 'granted') { return this.lifecycle; }
      } catch { return this.lifecycle; }
      const current = this.context.capture().properties;
      if (this.destroyed || revision !== this.lifecycleRevision || current.UserId !== snapshot.properties.UserId || current.LoginContextId !== snapshot.properties.LoginContextId) { return; }
      this.permissionRequiredKey = undefined;
      permissionMode = 'check';
    }
    this.stopLocationWatcher();
    if (permissionMode === 'request') { this.recoveryDelayMs = 1000; }
    const watcher: WatcherContext = { id: telemetryUuid(), key: contextKey, snapshot, permissionMode, sequence: 0 };
    this.active = watcher;
    this.setHealth('starting');
    this.lifecycle = this.lifecycle.then(async () => {
      if (this.active !== watcher) { return; }
      try {
        await this.removeRetiredWatcher();
        if (this.active !== watcher) { return; }
        if (!this.isNativePlatform) {
          const permission = await Geolocation.checkPermissions();
          if (permission.location !== 'granted') {
            if (watcher.permissionMode === 'check') { throw new Error('Location permission denied'); }
            const requested = await Geolocation.requestPermissions();
            if (requested.location !== 'granted') { throw new Error('Location permission denied'); }
          }
        }
        if (this.active !== watcher) { return; }
        await this.deviceInfo.getInputType();
        if (this.active !== watcher) { return; }
        this.diagnostic('Watcher.Started', watcher);
        const providerId = this.isNativePlatform ? await this.startAppLocationWatcher(watcher) : await this.startWebLocationWatcher(watcher);
        watcher.providerId = providerId;
        if (this.active !== watcher) {
          this.retired = watcher;
          await this.removeRetiredWatcher();
        } else {
          this.locationWatcherId = providerId;
          this.setHealth('active');
        }
      } catch (error) {
        this.reportError('provider', 'start', watcher.snapshot, watcher.id);
        this.recoverWatcher(watcher, error);
      }
    });
    return this.lifecycle;
  }

  async stopLocationWatcher() {
    this.lifecycleRevision++;
    clearTimeout(this.recoveryTimer);
    this.recoveryTimer = undefined;
    this.setHealth('stopped');
    const previous = this.active;
    this.active = undefined;
    if (this.retry && this.retry.watcher === previous) {
      this.retry.controller.abort();
      this.retry = undefined;
    }
    this.locationWatcherId = undefined;
    this.newestFix = undefined;
    this.appliedFix = undefined;
    this.appliedOrder = 0;
    if (previous) {
      this.eventBusService.emit({ type: AppEventType.USER_CURRENT_POSITION_LOGGED, data: { Code: 200, Message: 'Context ended', ContextEnded: true } });
      this.diagnostic('Watcher.Stopped', previous);
      this.lifecycle = this.lifecycle.then(async () => {
        if (previous.providerId) { this.retired = previous; }
        await this.removeRetiredWatcher();
      }).catch(() => this.reportError('provider', 'stop', previous.snapshot, previous.id));
    }
    return this.lifecycle;
  }

  async refetchUserCurrentLocation(){
    const watcher = this.active;
    if (!watcher || this.retry?.watcher === watcher) { return; }
    const retry = { watcher, controller: new AbortController() };
    this.retry = retry;
    let rejectCancelled: (error: Error) => void;
    const cancelled = new Promise<never>((_, reject) => rejectCancelled = reject);
    const onAbort = () => rejectCancelled(new Error('Verification context ended'));
    retry.controller.signal.addEventListener('abort', onAbort, { once: true });
    const order = ++this.verificationOrder;
    try {
      const position = await Promise.race([this.locationService.getLiveCurrentLocation(), cancelled]);
      if (this.active !== watcher) { return; }
      const fix = this.providerTimestamp(position);
      if (fix !== undefined && this.newestFix !== undefined && fix < this.newestFix) {
        throw new Error('Provider returned an older observation for verification');
      }
      const observation = this.captureObservation(position, 'Verification');
      if (!observation) { throw new Error('Invalid provider position'); }
      const result = await Promise.race([
        this.uploadCurrentLocation(observation, watcher, order, retry.controller.signal),
        cancelled
      ]);
      if (!result || this.active !== watcher || order < this.appliedOrder) { return; }
      if (result?.Result?.IsInProjectBoundary == null || result.Result.IsInProjectBoundary === UserPositionResult.NoMapping) {
        this.notificationsService.showToast({ message: 'Project boundary information is unavailable.', cssClass: TOAST_CLASS.WARNING });
      }
    } catch {
      if (retry.controller.signal.aborted) { return; }
      this.reportError('Verification', 'verification', watcher.snapshot, watcher.id);
      if (this.active === watcher && order >= this.appliedOrder) {
        this.notificationsService.showToast({ message: 'Unable to verify your location. Please try again.', cssClass: TOAST_CLASS.WARNING });
      }
    } finally {
      retry.controller.signal.removeEventListener('abort', onAbort);
      if (this.retry === retry) { this.retry = undefined; }
    }
  }

  private async startAppLocationWatcher(watcher: WatcherContext): Promise<string> {
    return this.geoLocationPlugin.addWatcher(
      {
        backgroundMessage: 'Your location is recorded in the background while you are clocked in.',
        backgroundTitle: 'You are Clocked In',
        requestPermissions: watcher.permissionMode === 'request',
        distanceFilter: 1,
      },
      async (position, error) => {
        if (this.active !== watcher) {
          return;
        }
        if (error) {
          this.reportError('NativeBackground', String(error.code || 'provider'), this.context.capture(), watcher.id);
          if (error.code === 'NOT_AUTHORIZED') {
            this.geoLocationPlugin.openSettings();
          }
          this.recoverWatcher(watcher, error);
          return;
        }
        if (position) {
          await this.ngZone.run(() => this.observe(watcher, {
            timestamp: position.time,
            coords: {
              latitude: position.latitude,
              longitude: position.longitude,
              accuracy: position.accuracy,
              altitude: position.altitude,
              altitudeAccuracy: position.altitudeAccuracy,
              heading: position.bearing,
              speed: position.speed,
              magneticHeading: null,
              trueHeading: null,
              headingAccuracy: null,
              course: null,
            },
          }, 'NativeBackground', position.simulated));
        }
      }
    );
  }

  private async startWebLocationWatcher(watcher: WatcherContext): Promise<string> {
    this.notificationsService.scheduleLocalNotification({
      id: LOCAL_NOTIFICATION_ACTION_TYPE.LOCATION_TRACKING,
      title: 'Location Tracking',
      body: 'Your location is recorded in the background while you are clocked in.',
      schedule: { at: new Date(Date.now() + 1000) },
    });

    return Geolocation.watchPosition({ timeout: GlobalConstants.GPS_TRACKING_MAX_TIMEOUT, maximumAge: 0, enableHighAccuracy: true }, async (position, error) => {
      if (this.active !== watcher) {
        return;
      }
      if (error) {
        this.reportError('BrowserWatch', String(error.code || 'provider'), this.context.capture(), watcher.id);
        this.recoverWatcher(watcher, error);
      }
      if (position) { await this.ngZone.run(() => this.observe(watcher, position, 'BrowserWatch')); }
    });
  }

  private async observe(watcher: WatcherContext, position: Position, source: string, simulated?: boolean): Promise<void> {
    if (this.active !== watcher || !hasValidCoordinates(position?.coords)) { return; }
    this.recoveryDelayMs = 1000;
    this.locationService.rememberCurrentLocation(position);
    const fix = this.providerTimestamp(position);
    if (fix !== undefined) {
      this.newestFix = this.newestFix === undefined ? fix : Math.max(this.newestFix, fix);
    }
    watcher.sequence++;
    const observation = this.captureObservation(position, source, watcher, simulated);
    const order = ++this.verificationOrder;
    let network: { connected: boolean };
    try { network = await this.deviceInfo.getNetworkStatus(); }
    catch { this.reportError(source, 'network', observation.context, watcher.id); return; }
    if (this.active !== watcher || !network.connected) { return; }
    const now = performance.now();
    if (watcher.lastAttempt !== undefined && now - watcher.lastAttempt < GlobalConstants.GPS_TRACKING_POLLING_INTERVAL) { return; }
    watcher.lastAttempt = now;
    try { await this.uploadCurrentLocation(observation, watcher, order); }
    catch { this.reportError(source, 'delivery', observation.context, watcher.id); }
  }

  private captureObservation(position: Position, source: string, watcher?: WatcherContext, simulated?: boolean): LocationObservation | null {
    if (!hasValidCoordinates(position?.coords)) { return null; }
    const context = this.context.capture();
    const fix = this.providerTimestamp(position);
    const timestamp = fix === undefined ? null : new Date(fix).toISOString();
    const quality = (value: number, nonnegative = true) => Number.isFinite(value) && (!nonnegative || value >= 0) ? value : null;
    return Object.freeze({ context, body: Object.freeze({
      PositionId: telemetryUuid(),
      InstallationId: context.properties.InstallationId,
      AppRunId: context.properties.AppRunId,
      LoginContextId: context.properties.LoginContextId,
      InputType: context.properties.InputType,
      AppVersion: context.version,
      AppBuild: context.properties.AppBuild,
      Platform: context.properties.Platform,
      OSVersion: context.properties.OSVersion,
      Model: context.model,
      WatcherId: watcher?.id,
      WatcherSequence: watcher?.sequence,
      PositionTimestamp: timestamp,
      ClientCapturedAt: new Date().toISOString(),
      AcquisitionSource: source,
      Latitude: position.coords.latitude,
      Longitude: position.coords.longitude,
      Accuracy: quality(position.coords.accuracy),
      Altitude: quality(position.coords.altitude, false),
      AltitudeAccuracy: quality(position.coords.altitudeAccuracy),
      Speed: quality(position.coords.speed),
      Heading: quality(position.coords.heading) <= 360 ? quality(position.coords.heading) : null,
      IsMock: simulated,
    }) });
  }

  private providerTimestamp(position: Position): number | undefined {
    return Number.isFinite(position?.timestamp) && Math.abs(position.timestamp) <= 8640000000000000 ? position.timestamp : undefined;
  }

  private async uploadCurrentLocation(observation: LocationObservation, watcher: WatcherContext, order: number, signal?: AbortSignal): Promise<LogUserPositionResponse | undefined> {
    const accessToken = this.sessions.getAccessToken({
      userId: Number(observation.context.properties.UserId),
      loginContextId: observation.context.properties.LoginContextId,
    });
    const current = this.context.capture().properties;
    if (!accessToken || this.active !== watcher || current.LoginContextId !== observation.context.properties.LoginContextId) {
      throw new Error('Obsolete account context');
    }
    const uri = environment.mobileApiUrl + GlobalConstants.LOG_USER_POSITION_URI;
    const attempt = this.telemetry.beginRequest('POST', uri, observation.context);
    const eventContext = { snapshot: observation.context, operation: attempt };
    let outcome: HttpDependencyOutcome = { type: 'network-error' };
    const webController = this.isNativePlatform ? undefined : new AbortController();
    const options = {
      url: uri,
      method: 'POST',
      connectTimeout: GlobalConstants.GPS_TRACKING_MAX_TIMEOUT,
      readTimeout: GlobalConstants.GPS_TRACKING_MAX_TIMEOUT,
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken, ...this.telemetry.correlationHeaders(uri, attempt) },
      data: { ...observation.body, ClientSentAt: new Date().toISOString() },
      webFetchExtra: this.telemetry.manualFetchOptions(attempt, { signal: webController?.signal }),
    };
    let rejectStopped: (error: Error) => void;
    const stopped = new Promise<never>((_, reject) => rejectStopped = reject);
    const onAbort = () => {
      outcome = { type: 'cancelled' };
      rejectStopped(new Error('Request context ended'));
      webController?.abort();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    const deadline = setTimeout(() => {
      outcome = { type: 'timeout' };
      rejectStopped(new Error('Location delivery timed out'));
      webController?.abort();
    }, GlobalConstants.GPS_TRACKING_MAX_TIMEOUT);
    try {
      if (signal?.aborted) {
        outcome = { type: 'cancelled' };
        throw new Error('Request context ended');
      }
      const response = await Promise.race([CapacitorHttp.request(options), stopped]);
      outcome = { type: 'http', status: response.status };
      const data: LogUserPositionResponse = response.data;
      if (response.status < 200 || response.status >= 300 || data?.Code !== 200) { throw new Error('Position verification failed'); }
      const fix = observation.body.PositionTimestamp ? Date.parse(String(observation.body.PositionTimestamp)) : undefined;
      if (this.active === watcher && order >= this.appliedOrder && (fix === undefined || this.appliedFix === undefined || fix >= this.appliedFix)) {
        this.appliedOrder = order;
        if (fix !== undefined) {
          this.appliedFix = fix;
          this.newestFix = this.newestFix === undefined ? fix : Math.max(this.newestFix, fix);
        }
        this.eventBusService.emit({ type: AppEventType.USER_CURRENT_POSITION_LOGGED, data });
        return data;
      }
      return undefined;
    } catch (error) {
      this.log.event('Location.UploadFailed', { PositionId: observation.body.PositionId, AttemptId: attempt.spanId, TraceId: attempt.traceId }, eventContext);
      if (this.active === watcher && order >= this.appliedOrder) {
        this.eventBusService.emit({ type: AppEventType.USER_CURRENT_POSITION_LOGGED, data: { Code: 0, Message: 'Position verification unavailable' } });
      }
      throw error;
    } finally {
      clearTimeout(deadline);
      signal?.removeEventListener('abort', onAbort);
      this.telemetry.completeRequest(attempt, outcome);
    }
  }

  private recoverWatcher(watcher: WatcherContext, error: unknown): void {
    if (this.destroyed || this.active !== watcher) { return; }
    void this.stopLocationWatcher();
    const details = error && typeof error === 'object' ? error as { code?: unknown; message?: unknown } : {};
    const permissionDenied = details.code === 'NOT_AUTHORIZED' || details.code === 1 || details.code === 'OS-PLUG-GLOC-0003' ||
      (typeof details.message === 'string' && /permission denied|not authorized/i.test(details.message));
    if (permissionDenied) {
      this.permissionRequiredKey = [watcher.snapshot.properties.UserId, watcher.snapshot.properties.LoginContextId].join(':');
      this.setHealth('permission-required');
      return;
    }
    this.setHealth('temporarily-unavailable');
    this.recoveryTimer = setTimeout(() => {
      this.recoveryTimer = undefined;
      const account = this.context.capture().properties;
      const state = this.appUserClockStateService.clockState;
      if (this.destroyed || this.active || account.UserId !== watcher.snapshot.properties.UserId || account.LoginContextId !== watcher.snapshot.properties.LoginContextId) { return; }
      const key = this.workingContextKey(state, account);
      if (key) { void this.startLocationWatcher(key, 'check'); }
    }, this.recoveryDelayMs);
    this.recoveryDelayMs = Math.min(60000, this.recoveryDelayMs * 2);
  }

  private setHealth(health: LocationTrackingHealth): void {
    this.ngZone.run(() => this.healthSubject.next(health));
  }

  private removeProviderWatcher(id: string): Promise<void> {
    return this.isNativePlatform ? this.geoLocationPlugin.removeWatcher({ id }) : Geolocation.clearWatch({ id });
  }

  private async removeRetiredWatcher(): Promise<void> {
    const retired = this.retired;
    if (!retired?.providerId) { return; }
    await this.removeProviderWatcher(retired.providerId);
    retired.providerId = undefined;
    if (this.retired === retired) { this.retired = undefined; }
  }

  private diagnostic(name: string, watcher: WatcherContext): void {
    this.log.event(`Location.${name}`, { WatcherId: watcher.id, Reason: 'Working context transition' }, { snapshot: watcher.snapshot });
  }

  private reportError(source: string, type: string, snapshot: TelemetrySnapshot, watcherId?: string): void {
    const key = [snapshot.properties.AppRunId, snapshot.properties.LoginContextId, source, type].join(':');
    let state = this.errors.get(key);
    if (!state) {
      if (this.errors.size >= 64) { const oldest = this.errors.keys().next().value; clearTimeout(this.errors.get(oldest)?.timer); this.errors.delete(oldest); }
      state = { count: 0 };
      this.errors.set(key, state);
      this.log.event('Location.Error', { Source: source, ErrorType: type, WatcherId: watcherId }, { snapshot });
    } else {
      state.count++;
      if (!state.timer) {
        state.timer = setTimeout(() => {
          this.log.event('Location.ErrorSummary', { Source: source, ErrorType: type, RepeatCount: state.count }, { snapshot });
          state.count = 0;
          state.timer = undefined;
        }, 60000);
      }
    }
  }
}
